import { profileAtom } from "@/atoms";
import { apiCustomer } from "@/api";
import { clearAccountToken } from "@/lib/account-token";
import { ensureGuestToken, guestProfile, currentGuestToken, hasRecoverableGuestSession, GUEST_SESSION_EVENT } from "@/lib/guest-session";
import { isAxiosError } from "axios";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";

export function useUserProfile() {
  const setProfile = useSetAtom(profileAtom);
  const hasLoadedRef = useRef(false);

  const loadUserProfile = useCallback(async () => {
    const token = await ensureGuestToken();

    if (!token) {
      if (hasRecoverableGuestSession()) return;
      setProfile(null);
      return;
    }

    try {
      const { data } = await apiCustomer.get("/me");

      const guest = guestProfile();
      if (!guest || data.id !== guest.id) return;
      setProfile({
        ...guest,
        id: data.id,
        name: data.name,
        phone: {
          number: data.phone,
        },
        addresses: data.addresses || [],
      });
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if ((status === 401 || status === 403) && currentGuestToken() === token) {
        clearAccountToken();
        setProfile(null);
      }
    }
  }, [setProfile]);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    loadUserProfile();
  }, [loadUserProfile]);

  useEffect(() => {
    const update = () => {
      if (currentGuestToken()) void loadUserProfile();
      else if (!hasRecoverableGuestSession()) setProfile(null);
    };
    window.addEventListener(GUEST_SESSION_EVENT, update);
    return () => window.removeEventListener(GUEST_SESSION_EVENT, update);
  }, [loadUserProfile, setProfile]);

  return { loadUserProfile };
}
